import { X } from 'lucide-react'
import type { ComponentType, ElementType, ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface OverlayHeaderProps {
  title?: ReactNode
  eyebrow?: string
  icon?: ComponentType<{ size?: number; className?: string }>
  badge?: ReactNode
  /**
   * Inline action(s) rendered to the left of the close button — e.g. a
   * "Reset" link inside a filters drawer, or a "Save" button on an
   * edit panel. Kept compact so the title stays the visual anchor.
   */
  actions?: ReactNode
  /**
   * What renders the visible title. Defaults to a plain `h2`.
   *
   * The dialog primitives pass Radix's `Dialog.Title` here so the heading a
   * sighted user reads IS the one the dialog is labelled by. Each of them used
   * to render the title twice — this header, plus an `sr-only` `Dialog.Title`
   * after the body — which announced every drawer, sheet and dialog title
   * twice to a screen reader, and made `getByRole('heading')` ambiguous in
   * every spec that reached for one.
   */
  titleAs?: ElementType
  /** A line under the title — what this overlay is for. */
  description?: ReactNode
  /**
   * What renders the description, as `titleAs` does for the title: a dialog
   * passes Radix's `Dialog.Description` so the line is what it is described by.
   */
  descriptionAs?: ElementType
  /** Larger title and roomier padding, for a form the operator works through. */
  spacious?: boolean
  onClose?: () => void
  closeLabel?: string
  className?: string
  children?: ReactNode
}

export function OverlayHeader({
  title,
  eyebrow,
  icon: Icon,
  badge,
  actions,
  titleAs: TitleTag = 'h2',
  description,
  descriptionAs: DescriptionTag = 'p',
  spacious,
  onClose,
  closeLabel = 'Close',
  className,
  children,
}: OverlayHeaderProps) {
  return (
    <header
      className={cn(
        'flex gap-3 border-b border-border-default',
        spacious ? 'px-7 pt-6 pb-5' : 'px-5 py-4',
        // With an eyebrow, align to the top of the icon so the eyebrow's
        // baseline sits where it belongs. Without an eyebrow, vertically
        // center the title against the icon.
        eyebrow || description ? 'items-start' : 'items-center',
        className,
      )}
    >
      {Icon && (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-page text-text-secondary">
          <Icon size={20} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="font-sans text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            {eyebrow}
          </div>
        )}
        <div className="flex items-center gap-2">
          {title && (
            <TitleTag
              className={cn(
                'font-semibold text-text-primary truncate',
                spacious ? 'text-xl' : 'text-base',
              )}
            >
              {title}
            </TitleTag>
          )}
          {badge && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-pill bg-surface-sunken text-text-primary">
              {badge}
            </span>
          )}
          {/* `actions` sits inline with the title (and badge) so a
              header-level action reads as part of the heading rather
              than as a separate row. The X close button stays
              anchored to the far right. */}
          {actions && (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          )}
        </div>
        {description ? (
          <DescriptionTag className="mt-1 text-[13px] text-text-muted">
            {description}
          </DescriptionTag>
        ) : null}
        {children}
      </div>

      {onClose && (
        <button
          type="button"
          aria-label={closeLabel}
          onClick={onClose}
          className="-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface-muted hover:text-text-primary transition-colors"
        >
          <X size={16} />
        </button>
      )}
    </header>
  )
}
